<template>
  <div class="container-fluid py-4">
    <div class="d-flex justify-content-center align-items-center mb-4">
      <Button class="start-0" :label="$t('Back')" route-name="CheckinCam" />
      <Brand width="150" height="150" />
    </div>

    <div class="row justify-content-center">
      <div class="col-12 col-xl-10">
        <div class="card shadow-sm border-0 rounded-custom mb-4">
          <div
            class="card-body p-4 d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3"
          >
            <div>
              <h2 class="text-violet text-uppercase mb-2">{{ $t("Milled elements registration") }}</h2>
              <p class="text-muted mb-0">
                {{ $t("Review the records already registered or start a new registration.") }}
              </p>
            </div>

            <button class="btn btn-violet text-uppercase" @click="$router.push({ name: 'CamProductionCreate' })">
              {{ $t("Register new record") }}
            </button>
          </div>
        </div>

        <div v-if="uiState === 'loading'" class="card shadow-sm border-0 rounded-custom p-5 text-center">
          <div class="spinner-border text-violet" role="status" />
          <p class="small text-muted mt-3 mb-0">{{ $t("Loading CAM records...") }}</p>
        </div>

        <div v-else-if="uiState === 'error'" class="card shadow-sm border-0 rounded-custom p-4">
          <div class="alert alert-danger mb-0">{{ pageError }}</div>
        </div>

        <template v-else>
          <div v-if="!logs.length && !hasActiveFilters" class="card shadow-sm border-0 rounded-custom p-5 text-center">
            <h3 class="text-violet h5 mb-2">{{ $t("No records registered") }}</h3>
            <p class="text-muted mb-0">
              {{ $t("When the first milled elements are registered, you will see them here.") }}
            </p>
          </div>

          <template v-else>
            <SummaryCards :cards="summaryCards" />

            <FiltersBar
              :search-query="searchQuery"
              :month-filter="monthFilter"
              :items-per-page="itemsPerPage"
              :exporting-format="exportingFormat"
              :is-exporting="isExporting"
              @update:search-query="searchQuery = $event"
              @update:month-filter="monthFilter = $event"
              @update:items-per-page="itemsPerPage = $event"
              @reset="resetFilters"
              @export="handleExport"
            />

            <div v-if="!logs.length" class="card shadow-sm border-0 rounded-custom p-5 text-center">
              <h3 class="text-violet h5 mb-2">{{ $t("No results") }}</h3>
              <p class="text-muted mb-0">
                {{ $t("Try changing the filters to find the records you are looking for.") }}
              </p>
            </div>

            <RecordsTable
              v-else
              :logs="logs"
              :current-page="currentPage"
              :total-pages="totalPages"
              :total-records="totalRecords"
              :visible-range-start="visibleRangeStart"
              :visible-range-end="visibleRangeEnd"
              :visible-pages="visiblePages"
              :format-date="formatDate"
              @preview="openPhotoPreview"
              @edit="openEditRecord"
              @change-page="changePage"
            />
          </template>
        </template>
      </div>
    </div>

    <PhotoPreviewModal
      :visible="isPhotoPreviewVisible"
      :url="selectedPhotoUrl"
      :label="selectedPhotoLabel"
      @close="closePhotoPreview"
      @download="downloadSelectedPhoto"
    />
  </div>
</template>

<script>
import { computed, onMounted, onBeforeUnmount, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import debounce from "lodash/debounce";
import Brand from "@/components/Brand.vue";
import Button from "@/components/general/Button.vue";
import FiltersBar from "@/components/cam/production-index/FiltersBar.vue";
import PhotoPreviewModal from "@/components/cam/production-index/PhotoPreviewModal.vue";
import RecordsTable from "@/components/cam/production-index/RecordsTable.vue";
import SummaryCards from "@/components/cam/production-index/SummaryCards.vue";
import { exportLogs, listLogs } from "@/use/repositories/camProduction";
import { firstErrorMessage } from "@/use/utilities/camProduction/firstErrorMessage";

export default {
  name: "CamProductionIndex",
  components: {
    Brand,
    Button,
    FiltersBar,
    PhotoPreviewModal,
    RecordsTable,
    SummaryCards,
  },
  setup() {
    const formatDateTime = (value) => new Date(value).toLocaleString("it-IT");
    const downloadFile = async (url, filename) => {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Download failed (${response.status})`);
      }

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(new Blob([blob], { type: blob.type || "image/jpeg" }));
      const link = document.createElement("a");

      link.href = objectUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(objectUrl);
    };
    const router = useRouter();
    const i18n = useI18n();
    const uiState = ref("loading");
    const pageError = ref("");
    const logs = ref([]);
    const summary = ref({
      totals: {
        records: 0,
        elements: 0,
        average_elements: 0,
        photo_coverage: 0,
      },
    });
    const paginationMeta = ref({
      current_page: 1,
      from: 0,
      last_page: 1,
      to: 0,
      total: 0,
    });
    const searchQuery = ref("");
    const monthFilter = ref("");
    const itemsPerPage = ref(10);
    const currentPage = ref(1);
    const exportingFormat = ref("");
    const isPhotoPreviewVisible = ref(false);
    const selectedPhoto = ref(null);

    const loadLogs = async ({ keepReadyState = false } = {}) => {
      if (!keepReadyState || uiState.value !== "ready") {
        uiState.value = "loading";
      }

      pageError.value = "";

      try {
        const response = await listLogs({
          month: monthFilter.value,
          search: searchQuery.value,
          page: currentPage.value,
          perPage: itemsPerPage.value,
        }, !keepReadyState);

        logs.value = response.logs ?? [];
        summary.value = response.summary ?? summary.value;
        paginationMeta.value = {
          ...paginationMeta.value,
          ...(response.meta ?? {}),
        };
        currentPage.value = paginationMeta.value.current_page || 1;
        uiState.value = "ready";
      } catch (error) {
        pageError.value = firstErrorMessage(error, i18n.t("Unable to load CAM records."));
        uiState.value = "error";
      }
    };

    const formatDate = (value) => value ? formatDateTime(value) : "-";

    const summaryCards = computed(() => [
      {
        label: i18n.t("Filtered records"),
        value: summary.value?.totals?.records ?? 0,
        caption: i18n.t("Total records in the selected scope"),
      },
      {
        label: i18n.t("Milled elements"),
        value: summary.value?.totals?.elements ?? 0,
        caption: i18n.t("Sum of recorded elements"),
      },
      {
        label: i18n.t("Average elements"),
        value: summary.value?.totals?.average_elements ?? 0,
        caption: i18n.t("Average per single record"),
      },
      {
        label: i18n.t("Photo coverage"),
        value: `${summary.value?.totals?.photo_coverage ?? 0}%`,
        caption: i18n.t("Share of records with an attached photo"),
      },
    ]);

    const totalRecords = computed(() => paginationMeta.value.total ?? 0);
    const totalPages = computed(() => Math.max(1, paginationMeta.value.last_page ?? 1));
    const visibleRangeStart = computed(() => paginationMeta.value.from ?? 0);
    const visibleRangeEnd = computed(() => paginationMeta.value.to ?? 0);
    const hasActiveFilters = computed(() => Boolean(searchQuery.value || monthFilter.value));

    const visiblePages = computed(() => {
      const start = Math.max(1, currentPage.value - 2);
      const end = Math.min(totalPages.value, start + 4);
      const adjustedStart = Math.max(1, end - 4);

      return Array.from({ length: end - adjustedStart + 1 }, (_, index) => adjustedStart + index);
    });

    const resetFilters = () => {
      searchQuery.value = "";
      monthFilter.value = "";
      itemsPerPage.value = 10;
      currentPage.value = 1;
    };

    const handleExport = async (format) => {
      exportingFormat.value = format;

      try {
        await exportLogs(
          {
            month: monthFilter.value,
            search: searchQuery.value,
          },
          format,
        );
      } catch (error) {
        pageError.value = firstErrorMessage(error, i18n.t("Unable to export CAM records."));
      } finally {
        exportingFormat.value = "";
      }
    };

    const openPhotoPreview = (log) => {
      selectedPhoto.value = log;
      isPhotoPreviewVisible.value = true;
    };

    const closePhotoPreview = () => {
      isPhotoPreviewVisible.value = false;
      selectedPhoto.value = null;
    };

    const downloadSelectedPhoto = async () => {
      if (!selectedPhoto.value?.photo_url) return;

      const safeDate = `${selectedPhoto.value.logged_at || ""}`.slice(0, 10) || "record";

      try {
        await downloadFile(selectedPhoto.value.photo_url, `cam-production-photo-${safeDate}.jpg`);
      } catch (error) {
        pageError.value = firstErrorMessage(error, i18n.t("Unable to download the photo."));
      }
    };

    const openEditRecord = (id) => {
      router.push({ name: "CamProductionEdit", params: { id } });
    };

    const changePage = (page) => {
      if (page < 1 || page > totalPages.value || page === currentPage.value) {
        return;
      }

      currentPage.value = page;
    };

    const selectedPhotoUrl = computed(() => selectedPhoto.value?.photo_url || "");
    const selectedPhotoLabel = computed(() =>
      selectedPhoto.value
        ? `${selectedPhoto.value.user_name || i18n.t("Operator")} - ${formatDate(selectedPhoto.value.logged_at)}`
        : "",
    );

    const debouncedReload = debounce(() => {
      loadLogs({ keepReadyState: true });
    }, 300);

    watch([searchQuery, monthFilter, itemsPerPage], () => {
      if (currentPage.value !== 1) {
        currentPage.value = 1;
        return;
      }

      debouncedReload();
    });

    watch(currentPage, () => {
      loadLogs({ keepReadyState: true });
    });

    onMounted(() => {
      loadLogs();
    });

    onBeforeUnmount(() => {
      debouncedReload.cancel();
    });

    return {
      uiState,
      pageError,
      logs,
      searchQuery,
      monthFilter,
      itemsPerPage,
      currentPage,
      totalRecords,
      totalPages,
      exportingFormat,
      isExporting: computed(() => exportingFormat.value !== ""),
      hasActiveFilters,
      summaryCards,
      visiblePages,
      visibleRangeStart,
      visibleRangeEnd,
      resetFilters,
      handleExport,
      isPhotoPreviewVisible,
      selectedPhotoUrl,
      selectedPhotoLabel,
      openPhotoPreview,
      closePhotoPreview,
      downloadSelectedPhoto,
      openEditRecord,
      changePage,
      formatDate,
    };
  },
};
</script>
